import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { IContentMonitoringEvent } from '../../enums/monitoring.enum';

export interface MonitoringUser {
  idBodega: number;
  nud: number;
}

@Injectable({
  providedIn: 'root'
})
export class MonitoringService {

  constructor(
    private translateS: TranslateService,
  ) { }

  public async initialize(): Promise<void> {
    // Asegurar que las traducciones estén cargadas
    await firstValueFrom(this.translateS.get('Sentry-Config'));

    // Obtener configuración remota con fallback a valores por defecto
    const tracesSampleRate = this.getConfigValue('Sentry-Config.traces-sample-rate', environment.sentry.tracesSampleRate, 0.1);
    const replaysSessionSampleRate = this.getConfigValue('Sentry-Config.replays-session-sample-rate', environment.sentry.replaysSessionSampleRate, 0.1);
    const replaysOnErrorSampleRate = this.getConfigValue('Sentry-Config.replays-on-error-sample-rate', environment.sentry.replaysOnErrorSampleRate, 0.1);

    // Construir release version
    const versionCode = this.getConfigValue('Build-Info.version-code', environment.versionCode, 0);
    const version = this.getConfigValue('Build-Info.version', environment.versionNumber, '0.0.0');
    const release = `${version}-${versionCode}`;

    Sentry.init({
      dsn: environment.sentry.dsn,
      environment: environment.sentry.environment,
      release: release,
      initialScope: { tags: { serviceName: environment.sentry.serviceName } },
      integrations: [
        Sentry.browserTracingIntegration()
      ],
      tracePropagationTargets: ["localhost", new RegExp(`^${environment.urlApiV2}`)],
      tracesSampleRate: tracesSampleRate,
      replaysSessionSampleRate: replaysSessionSampleRate,
      replaysOnErrorSampleRate: replaysOnErrorSampleRate,
      ignoreErrors: [
        'isProxied', 'ChunkLoadError', 'ERR_INTERNET_DISCONNECTED', // Ignorar errores comunes, estos por desconexión de internet en la navegación del app
        'messaging/unsupported-browser' // Ignorar errores de Firebase Messaging
      ],
    });
  }

  public setUser(user: MonitoringUser | null): void {
    Sentry.setUser(user);
  }

  public captureException(error: any): void {
    Sentry.captureException(error);
  }

  public captureMessage(event: IContentMonitoringEvent): void {
    Sentry.captureMessage(event.description, event.type);
  }

  /**
   * Obtiene un valor de configuración remoto con fallbacks
   */
  private getConfigValue<T>(key: string, envFallback: T, defaultValue: T): T {
    try {
      const remoteValue = this.translateS.instant(key);

      // Si no existe el valor remoto (devuelve la misma clave), usar fallbacks
      if (remoteValue === key || remoteValue === null || remoteValue === undefined || remoteValue === '') {
        return envFallback ?? defaultValue;
      }

      // Convertir el valor al tipo correcto
      const typedValue = remoteValue as T;
      return typedValue;
    } catch (error) {
      return envFallback ?? defaultValue;
    }
  }
}